import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import logoSeizo from '../images/logo-seizo.png';
import logo_seizo_s from '../images/svg/logo_seizo_s.svg';
import logo_seizo_nom from '../images/svg/logo_seizo_nom.svg';
import axiosUserInstance from '../axiosUser';

const API_URL = process.env.REACT_APP_API_URL || '';

// ─── Constantes de style ───────────────────────────────────────────────────────
const FONT        = '"Sofia Pro", "Helvetica Neue", Arial, sans-serif';
const SEIZO_BG    = '#f0f6fb';
const SEIZO_GREEN = '#66BE63';
const TEXT_DARK   = '#1a1f36';
const TEXT_LIGHT  = '#6b7280';
const CARD_BG     = '#ffffff';
const SEIZO_BLUE  = '#00B0FF';
const SEIZO_DARK  = '#002335';

// ─── Plans ────────────────────────────────────────────────────────────────────
const PLANS = [
    {
        key: 'basique',
        nom: 'SEIZO - Offre basique',
        prix: 0,
        ecransInclus: 1,
        essai: null,
        badge: 'Gratuit',
        description: "Idéal pour faire ses premiers pas dans l'écosystème SEIZO.",
        features: [
            "Accès limité à Mon Espace SEIZO (Console d'Admin)",
            'Accès à 1 écran pour 1 équipe',
            'Accès limité aux phidgets pour animer vos rituels',
        ],
        cta: 'Commencer gratuitement',
        hasStripe: false,
    },
    {
        key: 'premium',
        nom: 'SEIZO - Premium',
        prix: 39,
        ecransInclus: 1,
        essai: "14 jours d'essai gratuit",
        badge: null,
        description: "Abonnement pour un espace d'affichage, avec accès à une plateforme d'administration permettant de créer un nombre illimité d'utilisateurs.",
        features: [
            "Accès à la plateforme d'administration Mon Espace SEIZO",
            "Accès à 1 espace d'affichage avec utilisateurs illimités",
            'Accès à une bibliothèque de phidgets pour animer vos rituels',
            "Assistance par chat en ligne sur la rubrique Centre d'Aide",
            "14 jours d'essai gratuit",
        ],
        cta: 'Essayer gratuitement',
        hasStripe: true,
        highlighted: true,
    },
    {
        key: 'industriel',
        nom: 'SEIZO - Site industriel',
        prix: 149,
        ecransInclus: 5,
        essai: null,
        badge: null,
        description: "Abonnement pour 5 espaces d'affichage, avec accès à une plateforme d'administration multi-équipes.",
        features: [
            "Accès à la plateforme d'administration Mon Espace SEIZO",
            "Accès à 5 espaces d'affichage avec utilisateurs illimités",
            'Accès à une bibliothèque de phidgets pour animer vos rituels',
            "Assistance par chat en ligne sur la rubrique Centre d'Aide",
        ],
        cta: 'Souscrire',
        hasStripe: true,
    },
];

const PLAN_ORDER  = ['basique', 'premium', 'industriel'];
const PLAN_LABELS = { basique: 'Offre basique', premium: 'Premium', industriel: 'Site industriel' };

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
};
const setCookie = (name, value, days = 3650) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + (days / 365));
    document.cookie = `${name}=${value}; path=/; expires=${d.toUTCString()}`;
};
const getEntrepriseCode = () => {
    try {
        // Phase 2 : lire currentEntrepriseInfos (sans préfixe) avec fallback
        const raw = getCookie('currentEntrepriseInfos') || getCookie('_currentEntrepriseInfos');
        if (!raw) return null;
        const data = JSON.parse(decodeURIComponent(raw));
        return data?.code ?? null;
    } catch { return null; }
};

// ─── Styles partagés ──────────────────────────────────────────────────────────
const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
};
const modalStyle = {
    background: CARD_BG, borderRadius: '16px', padding: '2.5rem 3rem',
    width: '100%', maxWidth: '620px', maxHeight: '92vh', overflowY: 'auto',
    boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
};
const h3Style   = { margin: '0 0 1.2rem', color: TEXT_DARK, fontSize: '1.1rem', fontWeight: '700', fontFamily: FONT };
const btnGreen  = (loading) => ({ width: '100%', background: SEIZO_GREEN, color: '#fff', border: 'none', borderRadius: '6px', padding: '0.85rem', fontWeight: '600', fontSize: '1rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: FONT, marginBottom: '0.5rem' });
const btnOutline = { width: '100%', background: 'transparent', color: TEXT_DARK, border: '1.5px solid #d0d5dd', borderRadius: '6px', padding: '0.85rem', fontWeight: '600', fontSize: '1rem', cursor: 'pointer', fontFamily: FONT };
const btnBack   = { background: 'none', border: 'none', color: TEXT_LIGHT, cursor: 'pointer', marginBottom: '0.8rem', fontSize: '0.85rem', fontFamily: FONT, padding: 0 };
const btnCancel = { width: '100%', background: 'transparent', color: TEXT_LIGHT, border: 'none', padding: '0.5rem', fontFamily: FONT, cursor: 'pointer', fontSize: '0.85rem', marginTop: '0.3rem' };
const inputStyle = { width: '100%', padding: '0.65rem 0.9rem', borderRadius: '6px', border: '1.5px solid #d0d5dd', fontSize: '0.95rem', fontFamily: FONT, color: TEXT_DARK, outline: 'none', boxSizing: 'border-box', marginTop: '0.3rem' };
const labelStyle = { display: 'block', fontWeight: '600', fontSize: '0.85rem', color: TEXT_DARK, fontFamily: FONT };
const fieldStyle = { marginBottom: '1rem' };

// ─── Composants utilitaires ───────────────────────────────────────────────────
const PlanRecap = ({ plan, nbEcransSup, totalEcrans, prixTotal }) => (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.9rem 1.1rem', marginBottom: '1.4rem' }}>
        <div style={{ fontWeight: '700', color: TEXT_DARK, fontSize: '0.95rem' }}>{plan.nom}</div>
        <div style={{ color: TEXT_LIGHT, fontSize: '0.82rem', marginTop: '0.2rem' }}>
            {totalEcrans} écran(s) — <strong style={{ color: TEXT_DARK }}>{prixTotal === 0 ? 'Gratuit' : `${prixTotal}€/mois`}</strong>
            {plan.essai && <span style={{ color: SEIZO_GREEN, marginLeft: '0.5rem' }}>({plan.essai})</span>}
        </div>
    </div>
);

const ErrorBox = ({ msg }) => (
    <div style={{ background: '#fef2f2', color: '#c0392b', border: '1px solid #fca5a5', borderRadius: '6px', padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.88rem' }}>
        {msg}
    </div>
);

// ─── Modal inscription / connexion (pour non-connectés) ───────────────────────

const ModalCompte = ({ plan, nbEcransSup, onClose }) => {
    const [step, setStep]           = useState('choice');
    const [form, setForm]           = useState({ prenom: '', nom: '', email: '' });
    const [loginForm, setLoginForm] = useState({ email: '' });
    const [otp, setOtp]             = useState('');
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState('');

    const totalEcrans = plan.ecransInclus + nbEcransSup;
    const prixTotal   = plan.prix + (nbEcransSup * 39);

    const handleRegisterPaid = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            const res = await axios.post(`${API_URL}/api/stripe/checkout`, {
                plan_key: plan.key, nb_ecrans_supp: nbEcransSup,
                prenom: form.prenom, nom: form.nom, email: form.email,
            });
            window.location.href = res.data.checkout_url;
        } catch (err) {
            const validationErrors = err.response?.data?.errors;
            const firstValidationMsg = validationErrors ? Object.values(validationErrors).flat()[0] : null;
            setError(firstValidationMsg || err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
            setLoading(false);
        }
    };
    const handleRegisterBasique = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            const res = await axios.post(`${API_URL}/api/stripe/register-basique`, {
                plan_key: plan.key, nb_ecrans_supp: nbEcransSup,
                prenom: form.prenom, nom: form.nom, email: form.email,
            });
            setCookie('user_apiToken', res.data.token);
            setCookie('currentUserInfos', JSON.stringify({
                nom: res.data.user.nom, prenom: res.data.user.prenom, email: res.data.user.email,
                admin_teis: null, nb_entreprises: 1,
            }));
            window.location.href = `/${res.data.entreprise_code}`;
        } catch (err) {
            const validationErrors = err.response?.data?.errors;
            const firstValidationMsg = validationErrors ? Object.values(validationErrors).flat()[0] : null;
            setError(firstValidationMsg || err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            await axios.get(`${API_URL}/api/otp`, { params: { email: loginForm.email } });
            setStep('otp');
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur lors de la connexion.');
            setLoading(false);
        }
    };

    const handleOtp = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            const res = await axios.post(`${API_URL}/api/otp`, { email: loginForm.email, otp });
            if (res.data.status) {
                const token = res.data.token;
                setCookie('user_apiToken', token);
                if (res.data.user) {
                    setCookie('currentUserInfos', JSON.stringify({
                        nom: res.data.user.nom, prenom: res.data.user.prenom,
                        email: res.data.user.email, admin_teis: null, nb_entreprises: 1,
                    }));
                }
                if (plan.hasStripe) {
                    const checkRes = await axios.post(
                        `${API_URL}/api/stripe/checkout`,
                        { plan_key: plan.key, nb_ecrans_supp: nbEcransSup },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    window.location.href = checkRes.data.checkout_url;
                } else {
                    window.location.reload();
                }
            } else {
                setError('Code OTP invalide.');
                setLoading(false);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Code invalide.');
            setLoading(false);
        }
    };

    return (
        <div style={overlayStyle}>
            <div style={modalStyle}>
                <PlanRecap plan={plan} nbEcransSup={nbEcransSup} totalEcrans={totalEcrans} prixTotal={prixTotal} />

                {step === 'choice' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <button onClick={() => setStep('register')} style={btnGreen(false)}>
                            {plan.key === 'basique' ? 'Créer mon compte gratuitement' : 'Créer un compte'}
                        </button>
                        {plan.key !== 'basique' && (
                            <button onClick={() => setStep('login')} style={btnOutline}>
                                J'ai déjà un compte
                            </button>
                        )}
                        <button onClick={onClose} style={btnCancel}>Annuler</button>
                    </div>
                )}

                {step === 'register' && (
                    <>
                        <button onClick={() => setStep('choice')} style={btnBack}>← Retour</button>
                        <h3 style={h3Style}>{plan.key === 'basique' ? 'Créer votre compte gratuit' : 'Créer votre compte'}</h3>
                        {error && <ErrorBox msg={error} />}
                        <form onSubmit={plan.key === 'basique' ? handleRegisterBasique : handleRegisterPaid}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Prénom *</label>
                                    <input required value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} style={inputStyle} />
                                </div>
                                <div style={fieldStyle}>
                                    <label style={labelStyle}>Nom *</label>
                                    <input required value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} style={inputStyle} />
                                </div>
                            </div>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Email *</label>
                                <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} />
                            </div>
                            <button type="submit" disabled={loading} style={btnGreen(loading)}>
                                {loading
                                    ? (plan.key === 'basique' ? 'Création du compte...' : 'Redirection vers le paiement...')
                                    : (plan.key === 'basique' ? 'Créer mon compte gratuitement →' : 'Continuer vers le paiement →')
                                }
                            </button>
                        </form>
                        <button onClick={onClose} style={btnCancel}>Annuler</button>
                    </>
                )}

                {step === 'login' && (
                    <>
                        <button onClick={() => setStep('choice')} style={btnBack}>← Retour</button>
                        <h3 style={h3Style}>Connexion à votre compte</h3>
                        {error && <ErrorBox msg={error} />}
                        <form onSubmit={handleLogin}>
                            <div style={{ ...fieldStyle, marginBottom: '1.5rem' }}>
                                <label style={labelStyle}>Email *</label>
                                <input required type="email" value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} style={inputStyle} />
                            </div>
                            <button type="submit" disabled={loading} style={{ ...btnGreen(loading), marginTop: '1.5rem' }}>
                                {loading ? 'Envoi du code...' : 'Recevoir mon code de connexion →'}
                            </button>
                        </form>
                        <button onClick={onClose} style={btnCancel}>Annuler</button>
                    </>
                )}

                {step === 'otp' && (
                    <>
                        <button onClick={() => setStep('login')} style={btnBack}>← Retour</button>
                        <h3 style={h3Style}>Code de vérification</h3>
                        <p style={{ color: TEXT_LIGHT, fontSize: '0.88rem', marginBottom: '1rem' }}>
                            Un code a été envoyé à <strong>{loginForm.email}</strong>. Saisissez-le ci-dessous.
                        </p>
                        {error && <ErrorBox msg={error} />}
                        <form onSubmit={handleOtp}>
                            <div style={{ ...fieldStyle, marginBottom: '1.5rem' }}>
                                <label style={labelStyle}>Code OTP *</label>
                                <input type="text" required value={otp} onChange={e => setOtp(e.target.value)}
                                    style={{ ...inputStyle, letterSpacing: '0.2em', fontSize: '1.2rem', textAlign: 'center' }}
                                    placeholder="000000" maxLength={6} />
                            </div>
                            <button type="submit" disabled={loading} style={btnGreen(loading)}>
                                {loading ? 'Vérification...' : 'Valider et payer →'}
                            </button>
                        </form>
                        <button onClick={onClose} style={btnCancel}>Annuler</button>
                    </>
                )}
            </div>
        </div>
    );
};

// ─── Carte plan (mode non-connecté) ───────────────────────────────────────────
const PlanCard = ({ plan, nbEcransSup, onChangeSup, onSelect }) => {
    const totalEcrans = plan.ecransInclus + nbEcransSup;
    const prixTotal   = plan.prix + (nbEcransSup * 39);
    return (
        <div style={{
            background: CARD_BG,
            border: plan.highlighted ? `2px solid ${SEIZO_GREEN}` : '1px solid #e0e4ea',
            borderRadius: '12px',
            boxShadow: plan.highlighted ? '0 4px 24px rgba(102,190,99,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
            display: 'flex', flexDirection: 'column',
            minWidth: '260px', maxWidth: '320px', flex: '1',
            position: 'relative', overflow: 'hidden',
        }}>
            {(plan.badge || plan.essai) && (
                <div style={{
                    background: plan.highlighted ? SEIZO_GREEN : '#f0f6fb',
                    color: plan.highlighted ? '#fff' : TEXT_LIGHT,
                    fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.05em',
                    padding: '0.25rem 0.75rem', textAlign: 'center', fontFamily: FONT,
                }}>
                    {plan.badge || plan.essai}
                </div>
            )}
            <div style={{ padding: '1.6rem 1.6rem 1.2rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '700', color: TEXT_LIGHT, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.4rem', fontFamily: FONT }}>
                    {plan.nom}
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.2rem', marginBottom: '0.6rem' }}>
                    {plan.prix === 0 ? (
                        <span style={{ fontSize: '2.2rem', fontWeight: '800', color: TEXT_DARK, fontFamily: FONT }}>Gratuit</span>
                    ) : (
                        <>
                            <span style={{ fontSize: '1rem', fontWeight: '700', color: TEXT_DARK, alignSelf: 'flex-start', marginTop: '0.5rem', fontFamily: FONT }}>€</span>
                            <span style={{ fontSize: '2.8rem', fontWeight: '800', color: TEXT_DARK, lineHeight: 1, fontFamily: FONT }}>{prixTotal}</span>
                            <span style={{ fontSize: '0.85rem', color: TEXT_LIGHT, marginBottom: '0.4rem', fontFamily: FONT }}>/mois</span>
                        </>
                    )}
                </div>
                <p style={{ fontSize: '0.8rem', color: TEXT_LIGHT, margin: '0 0 1rem', lineHeight: 1.5, fontFamily: FONT }}>{plan.description}</p>
                {plan.hasStripe && (
                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', color: TEXT_LIGHT, marginBottom: '0.4rem', fontFamily: FONT }}>
                            Écrans supplémentaires (+39€/écran/mois)
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <button onClick={() => onChangeSup(Math.max(0, nbEcransSup - 1))} style={{ width: '26px', height: '26px', borderRadius: '50%', border: '1.5px solid #d0d5dd', background: '#fff', color: TEXT_DARK, cursor: 'pointer', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                            <span style={{ fontWeight: '700', fontSize: '1.1rem', color: TEXT_DARK, minWidth: '18px', textAlign: 'center' }}>{nbEcransSup}</span>
                            <button onClick={() => onChangeSup(nbEcransSup + 1)} style={{ width: '26px', height: '26px', borderRadius: '50%', border: 'none', background: SEIZO_GREEN, color: '#fff', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                            <span style={{ fontSize: '0.78rem', color: TEXT_LIGHT }}>= {totalEcrans} écran(s)</span>
                        </div>
                    </div>
                )}
                <button
                    onClick={() => onSelect(plan)}
                    style={{ width: '100%', background: plan.highlighted ? SEIZO_GREEN : (plan.prix === 0 ? '#1a1f36' : SEIZO_GREEN), color: '#fff', border: 'none', borderRadius: '6px', padding: '0.8rem', fontWeight: '600', fontSize: '0.95rem', cursor: 'pointer', fontFamily: FONT, transition: 'opacity 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                    {plan.cta}
                </button>
            </div>
            <div style={{ height: '1px', background: '#e8edf2', margin: '0 1.6rem' }} />
            <div style={{ padding: '1.2rem 1.6rem', flex: 1 }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                    {plan.features.map(f => (
                        <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: TEXT_DARK, lineHeight: 1.5 }}>
                            <span style={{ color: SEIZO_GREEN, fontWeight: '700', flexShrink: 0, marginTop: '1px' }}>✓</span>
                            {f}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

// ─── Mode connecté : Gestion abonnement ───────────────────────────────────────
const GestionAbonnement = ({ entrepriseCode, onLogout, onRetour }) => {
    const history  = useHistory();
    const location = useLocation();

    const [data,              setData]              = useState(null);
    const [abonnement,        setAbonnement]        = useState(null);
    const [loading,           setLoading]           = useState(true);
    const [loadingAbo,        setLoadingAbo]        = useState(false);
    const [nbEcransSup,       setNbEcransSup]       = useState(0);
    const [savingEcrans,      setSavingEcrans]      = useState(false);
    const [loadingPortail,    setLoadingPortail]    = useState(false);
    const [loadingResilier,   setLoadingResilier]   = useState(false);
    const [loadingReactiver,  setLoadingReactiver]  = useState(false);
    const [loadingUpgrade,    setLoadingUpgrade]    = useState(null);
    const [alert,             setAlert]             = useState(null);
    const [nbEcransUpgrade,   setNbEcransUpgrade]   = useState({});

    const headers = entrepriseCode ? { 'X-Entreprise-Code': entrepriseCode } : {};

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('success') === '1') {
            setAlert({ type: 'success', message: 'Votre abonnement a bien été mis à jour !' });
            // Re-fetch abonnement après délai pour laisser le webhook Stripe traiter
            const doRefresh = () => {
                axiosUserInstance.get('/api/stripe/abonnement', { headers })
                    .then(r => {
                        setAbonnement(r.data.abonnement);
                        setNbEcransSup(r.data.abonnement?.nb_ecrans_supp || 0);
                    })
                    .catch(() => {});
                axiosUserInstance.get('/api/mon-compte/espace', { headers })
                    .then(r => { setData(r.data); setLoading(false); })
                    .catch(() => {});
            };
            // Premier refresh après 3s, second après 8s (au cas où le webhook est lent)
            const t1 = setTimeout(doRefresh, 3000);
            const t2 = setTimeout(doRefresh, 8000);
            return () => { clearTimeout(t1); clearTimeout(t2); };
        }
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        axiosUserInstance.get('/api/mon-compte/espace', { headers })
            .then(r => { setData(r.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Polling abonnement après paiement : retry jusqu'à 6x si offre encore basique
    useEffect(() => {
        if (!loading && data) {
            const params = new URLSearchParams(location.search);
            const isPostPayment = params.get('success') === '1';
            let retries = 0;
            const maxRetries = isPostPayment ? 6 : 0;
            const initialDelay = isPostPayment ? 2000 : 0;
            const fetchAbo = () => {
                setLoadingAbo(true);
                axiosUserInstance.get('/api/stripe/abonnement', { headers })
                    .then(r => {
                        const abo = r.data.abonnement;
                        setAbonnement(abo);
                        setNbEcransSup(abo?.nb_ecrans_supp || 0);
                        // Si post-paiement et offre encore basique/null, retry
                        if (isPostPayment && retries < maxRetries && (!abo || abo.offre === 'basique' || abo.offre === null)) {
                            retries++;
                            setTimeout(fetchAbo, 2500);
                        } else {
                            setLoadingAbo(false);
                        }
                    })
                    .catch(() => setLoadingAbo(false));
            };
            if (initialDelay > 0) {
                setTimeout(fetchAbo, initialDelay);
            } else {
                fetchAbo();
            }
        }
    }, [loading, data]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpdateEcrans = async () => {
        setSavingEcrans(true); setAlert(null);
        try {
            const res = await axiosUserInstance.post('/api/stripe/abonnement/ecrans', { nb_ecrans_supp: nbEcransSup }, { headers });
            setAlert({ type: 'success', message: `Écrans mis à jour : ${res.data.nb_ecrans_total} écran(s) actif(s).` });
            const r = await axiosUserInstance.get('/api/stripe/abonnement', { headers });
            setAbonnement(r.data.abonnement);
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur lors de la mise à jour des écrans.' });
        } finally { setSavingEcrans(false); }
    };

    const handlePortail = async () => {
        setLoadingPortail(true);
        try {
            const res = await axiosUserInstance.post('/api/stripe/abonnement/portail', {}, { headers });
            window.open(res.data.url, '_blank');
        } catch {
            setAlert({ type: 'error', message: "Impossible d'ouvrir le portail de facturation." });
        } finally { setLoadingPortail(false); }
    };

    const handleResilier = async () => {
        if (!window.confirm("Confirmer la résiliation ? Votre abonnement restera actif jusqu'à la fin de la période en cours.")) return;
        setLoadingResilier(true);
        try {
            await axiosUserInstance.post('/api/stripe/abonnement/resilier', {}, { headers });
            setAlert({ type: 'success', message: "Résiliation programmée. Votre accès reste actif jusqu'à la fin de la période." });
            setAbonnement({ ...abonnement, cancel_at_period_end: true });
        } catch {
            setAlert({ type: 'error', message: 'Erreur lors de la résiliation.' });
        } finally { setLoadingResilier(false); }
    };

    const handleReactiver = async () => {
        setLoadingReactiver(true);
        try {
            await axiosUserInstance.post('/api/stripe/abonnement/reactiver', {}, { headers });
            setAlert({ type: 'success', message: 'Abonnement réactivé avec succès.' });
            setAbonnement({ ...abonnement, cancel_at_period_end: false });
        } catch {
            setAlert({ type: 'error', message: 'Erreur lors de la réactivation.' });
        } finally { setLoadingReactiver(false); }
    };

    const handleUpgrade = async (planKey) => {
        setLoadingUpgrade(planKey); setAlert(null);
        try {
            const nbSup = nbEcransUpgrade[planKey] || 0;
            const res = await axiosUserInstance.post('/api/stripe/checkout', { plan_key: planKey, nb_ecrans_supp: nbSup }, { headers });
            window.location.href = res.data.checkout_url;
        } catch (e) {
            setAlert({ type: 'error', message: e.response?.data?.message || 'Erreur lors de la redirection vers le paiement.' });
            setLoadingUpgrade(null);
        }
    };

    const currentPlanKey = abonnement?.plan_key || data?.licence?.offre || 'basique';
    const currentPlanIdx = PLAN_ORDER.indexOf(currentPlanKey);
    const upgradePlans   = PLANS.filter(p => PLAN_ORDER.indexOf(p.key) > currentPlanIdx && p.hasStripe);
    const isAdmin        = data?.role?.id <= 2;

    // ── Composants locaux ──
    const Card = ({ children, style: cardStyle = {} }) => (
        <div style={{ background: '#fff', borderRadius: '12px', padding: '1.8rem', boxShadow: '0 2px 12px rgba(0,35,53,0.07)', marginBottom: '1.5rem', ...cardStyle }}>
            {children}
        </div>
    );

    const BtnPrimary = ({ children, onClick, loading: btnLoading = false, style: btnStyle = {} }) => (
        <button onClick={onClick} disabled={btnLoading} style={{
            background: SEIZO_BLUE, color: '#fff', border: 'none', borderRadius: '8px',
            padding: '0.6rem 1.4rem', fontWeight: 700, fontSize: '0.9rem',
            cursor: btnLoading ? 'not-allowed' : 'pointer', opacity: btnLoading ? 0.7 : 1,
            transition: 'opacity 0.2s', ...btnStyle,
        }}>
            {btnLoading ? 'Chargement...' : children}
        </button>
    );

    const AlertBox = ({ type, message }) => {
        if (!message) return null;
        const colors = {
            success: { bg: '#e6f9ed', border: '#66BE63', text: '#1a6b2f' },
            error:   { bg: '#fdecea', border: '#F82121', text: '#8b1a1a' },
        };
        const c = colors[type] || colors.error;
        return (
            <div style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: '8px', padding: '0.7rem 1rem', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                {message}
            </div>
        );
    };

    if (loading) return (
        <div style={{ minHeight: '100vh', background: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: '#888' }}>Chargement...</p>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: 'inherit' }}>
            {/* Header identique à Mon Compte */}
            <div style={{
                background: SEIZO_DARK, padding: '0 2rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                height: '60px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', cursor: 'pointer' }} onClick={onRetour}>
                    <img src={logo_seizo_s}   alt="SEIZO" style={{ height: '32px' }} />
                    <img src={logo_seizo_nom} alt="SEIZO" style={{ height: '18px' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button onClick={() => history.push('/mon-compte')} style={{
                        background: 'transparent', border: '1px solid rgba(255,255,255,0.3)',
                        color: '#fff', borderRadius: '8px', padding: '0.4rem 1rem',
                        cursor: 'pointer', fontSize: '0.85rem',
                    }}>
                        Mon Compte
                    </button>
                    <button onClick={onRetour} style={{
                        background: 'transparent', border: '1px solid rgba(255,255,255,0.3)',
                        color: '#fff', borderRadius: '8px', padding: '0.4rem 1rem',
                        cursor: 'pointer', fontSize: '0.85rem',
                    }}>
                        ← Retour
                    </button>
                    <button onClick={onLogout} style={{
                        background: '#F82121', border: 'none',
                        color: '#fff', borderRadius: '8px', padding: '0.4rem 1rem',
                        cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                    }}>
                        Déconnexion
                    </button>
                </div>
            </div>

            <div style={{ maxWidth: '860px', margin: '0 auto', padding: '2.5rem 1.5rem' }}>
                <h1 style={{ color: SEIZO_DARK, marginTop: 0, marginBottom: '0.5rem', fontSize: '1.6rem', fontWeight: 800 }}>
                    Mon Abonnement
                </h1>
                <p style={{ color: TEXT_LIGHT, marginBottom: '2rem', fontSize: '0.95rem' }}>
                    Gérez votre offre, vos écrans et votre facturation.
                </p>

                <AlertBox type={alert?.type} message={alert?.message} />

                {/* ── Abonnement actuel ── */}
                <Card>
                    <h3 style={{ marginTop: 0, marginBottom: '1.2rem', color: SEIZO_DARK, fontSize: '1.1rem' }}>Abonnement actuel</h3>
                    {loadingAbo ? (
                        <p style={{ color: '#888', fontSize: '0.9rem' }}>Chargement de l'abonnement...</p>
                    ) : (
                        <>
                            {/* Badges statut */}
                            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '1.5rem', alignItems: 'center' }}>
                                <span style={{
                                    background: data?.isFreemium ? '#FF9436' : SEIZO_GREEN,
                                    color: '#fff', borderRadius: '20px', padding: '0.3rem 1rem',
                                    fontWeight: 700, fontSize: '0.85rem',
                                }}>
                                    {PLAN_LABELS[currentPlanKey] || currentPlanKey}
                                </span>
                                {abonnement?.statut === 'trialing' && (
                                    <span style={{ background: '#e8f4fd', color: SEIZO_BLUE, borderRadius: '20px', padding: '0.3rem 1rem', fontWeight: 600, fontSize: '0.85rem' }}>
                                        Essai gratuit
                                    </span>
                                )}
                                {abonnement?.cancel_at_period_end && (
                                    <span style={{ background: '#fdecea', color: '#F82121', borderRadius: '20px', padding: '0.3rem 1rem', fontWeight: 600, fontSize: '0.82rem' }}>
                                        Résiliation prévue le {abonnement.current_period_end}
                                    </span>
                                )}
                            </div>

                            {/* Métriques */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                                {[
                                    { label: 'Offre souscrite', value: PLAN_LABELS[abonnement?.plan_key] || data?.licence?.offre || '—' },
                                    { label: 'Écrans actifs', value: `${data?.nb_ecrans ?? 0} / ${data?.nb_max_totem ?? 1}` },
                                    { label: 'Prochain renouvellement', value: abonnement?.current_period_end || data?.licence?.date_fin || 'illimitée' },
                                ].map(item => (
                                    <div key={item.label} style={{ background: '#f5f8fc', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.4rem' }}>{item.label}</div>
                                        <div style={{ fontWeight: 700, color: SEIZO_DARK, fontSize: '1rem' }}>{item.value}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Essai gratuit */}
                            {abonnement?.trial_end && (
                                <div style={{ background: '#e6f9ed', borderRadius: '10px', padding: '0.8rem 1rem', marginBottom: '1.2rem', fontSize: '0.88rem', color: '#1a6b2f' }}>
                                    ✓ Essai gratuit actif jusqu'au <strong>{abonnement.trial_end}</strong> — aucun débit avant cette date.
                                </div>
                            )}

                            {/* Montant mensuel */}
                            {abonnement && (
                                <div style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#555' }}>
                                    Montant mensuel : <strong style={{ color: SEIZO_DARK, fontSize: '1.1rem' }}>{abonnement.montant_mensuel}€</strong>
                                    {abonnement.nb_ecrans_supp > 0 && (
                                        <span style={{ color: '#888', fontSize: '0.82rem' }}> (dont {abonnement.nb_ecrans_supp} écran(s) supplémentaire(s) × 39€)</span>
                                    )}
                                </div>
                            )}

                            {/* Gestion écrans supplémentaires */}
                            {isAdmin && abonnement && (
                                <div style={{ background: '#f5f8fc', borderRadius: '12px', padding: '1.2rem', marginBottom: '1.5rem' }}>
                                    <div style={{ fontWeight: 700, color: SEIZO_DARK, marginBottom: '0.8rem', fontSize: '0.9rem' }}>Ajouter des écrans supplémentaires</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                            <button onClick={() => setNbEcransSup(Math.max(0, nbEcransSup - 1))} style={{ width: '30px', height: '30px', borderRadius: '50%', border: '2px solid #dde3e9', background: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                                            <span style={{ fontWeight: 800, fontSize: '1.3rem', color: SEIZO_BLUE, minWidth: '24px', textAlign: 'center' }}>{nbEcransSup}</span>
                                            <button onClick={() => setNbEcransSup(nbEcransSup + 1)} style={{ width: '30px', height: '30px', borderRadius: '50%', border: `2px solid ${SEIZO_BLUE}`, background: SEIZO_BLUE, fontWeight: 700, cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>+</button>
                                        </div>
                                        <span style={{ fontSize: '0.85rem', color: '#888' }}>écran(s) sup. × 39€/mois</span>
                                        <BtnPrimary onClick={handleUpdateEcrans} loading={savingEcrans} style={{ marginLeft: 'auto' }}>
                                            Appliquer
                                        </BtnPrimary>
                                    </div>
                                </div>
                            )}

                            {/* Actions abonnement */}
                            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                {abonnement && isAdmin && (
                                    <BtnPrimary onClick={handlePortail} loading={loadingPortail}>
                                        Gérer la facturation
                                    </BtnPrimary>
                                )}
                                {abonnement && isAdmin && !abonnement.cancel_at_period_end && (
                                    <button onClick={handleResilier} disabled={loadingResilier} style={{
                                        background: 'none', border: '1.5px solid #F82121', color: '#F82121',
                                        borderRadius: '8px', padding: '0.55rem 1.2rem', fontWeight: 600,
                                        fontSize: '0.88rem', cursor: 'pointer', opacity: loadingResilier ? 0.6 : 1,
                                    }}>
                                        {loadingResilier ? 'Traitement...' : "Résilier l'abonnement"}
                                    </button>
                                )}
                                {abonnement && isAdmin && abonnement.cancel_at_period_end && (
                                    <BtnPrimary onClick={handleReactiver} loading={loadingReactiver}>
                                        Annuler la résiliation
                                    </BtnPrimary>
                                )}
                            </div>
                        </>
                    )}
                </Card>

                {/* ── Changer d'offre (upgrade) ── */}
                {upgradePlans.length > 0 && isAdmin && (
                    <Card>
                        <h3 style={{ marginTop: 0, marginBottom: '0.5rem', color: SEIZO_DARK, fontSize: '1.1rem' }}>Changer d'offre</h3>
                        <p style={{ color: TEXT_LIGHT, fontSize: '0.88rem', marginBottom: '1.5rem' }}>
                            Passez à une offre supérieure pour débloquer plus d'écrans et de fonctionnalités.
                        </p>
                        <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap' }}>
                            {upgradePlans.map(plan => {
                                const nbSup       = nbEcransUpgrade[plan.key] || 0;
                                const totalEcrans = plan.ecransInclus + nbSup;
                                const prixTotal   = plan.prix + (nbSup * 39);
                                return (
                                    <div key={plan.key} style={{
                                        flex: '1', minWidth: '220px', maxWidth: '340px',
                                        border: `2px solid ${SEIZO_GREEN}`, borderRadius: '12px',
                                        padding: '1.4rem', background: '#f9fffe',
                                    }}>
                                        <div style={{ fontWeight: 800, color: SEIZO_DARK, fontSize: '1rem', marginBottom: '0.3rem' }}>
                                            {plan.nom}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.2rem', marginBottom: '0.5rem' }}>
                                            <span style={{ fontSize: '2rem', fontWeight: 800, color: SEIZO_DARK, lineHeight: 1 }}>{prixTotal}</span>
                                            <span style={{ fontSize: '0.85rem', color: TEXT_LIGHT, marginBottom: '0.2rem' }}>€/mois</span>
                                        </div>
                                        <p style={{ fontSize: '0.8rem', color: TEXT_LIGHT, margin: '0 0 1rem', lineHeight: 1.5 }}>{plan.description}</p>
                                        <div style={{ marginBottom: '1rem' }}>
                                            <div style={{ fontSize: '0.75rem', color: TEXT_LIGHT, marginBottom: '0.4rem' }}>
                                                Écrans supplémentaires (+39€/écran/mois)
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                                <button onClick={() => setNbEcransUpgrade(prev => ({ ...prev, [plan.key]: Math.max(0, (prev[plan.key] || 0) - 1) }))} style={{ width: '26px', height: '26px', borderRadius: '50%', border: '1.5px solid #d0d5dd', background: '#fff', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                                                <span style={{ fontWeight: 700, fontSize: '1.1rem', color: SEIZO_DARK, minWidth: '18px', textAlign: 'center' }}>{nbSup}</span>
                                                <button onClick={() => setNbEcransUpgrade(prev => ({ ...prev, [plan.key]: (prev[plan.key] || 0) + 1 }))} style={{ width: '26px', height: '26px', borderRadius: '50%', border: 'none', background: SEIZO_GREEN, color: '#fff', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                                                <span style={{ fontSize: '0.78rem', color: TEXT_LIGHT }}>= {totalEcrans} écran(s)</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleUpgrade(plan.key)}
                                            disabled={loadingUpgrade === plan.key}
                                            style={{ width: '100%', background: SEIZO_GREEN, color: '#fff', border: 'none', borderRadius: '8px', padding: '0.75rem', fontWeight: 700, fontSize: '0.95rem', cursor: loadingUpgrade === plan.key ? 'not-allowed' : 'pointer', opacity: loadingUpgrade === plan.key ? 0.7 : 1 }}
                                        >
                                            {loadingUpgrade === plan.key ? 'Redirection...' : `Passer à ${PLAN_LABELS[plan.key]} →`}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                )}

                {/* ── Offre basique : incitation à upgrader ── */}
                {currentPlanKey === 'basique' && (
                    <Card style={{ background: 'linear-gradient(135deg, #002335 0%, #003a55 100%)', color: '#fff' }}>
                        <h3 style={{ marginTop: 0, color: '#fff', fontSize: '1.1rem' }}>Passez à la vitesse supérieure</h3>
                        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                            Débloquez l'accès complet aux phidgets, aux utilisateurs illimités et à l'assistance en ligne.
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            {PLANS.filter(p => p.hasStripe).map(plan => (
                                <button key={plan.key} onClick={() => handleUpgrade(plan.key)} disabled={loadingUpgrade === plan.key} style={{
                                    background: SEIZO_GREEN, color: '#fff', border: 'none', borderRadius: '8px',
                                    padding: '0.7rem 1.5rem', fontWeight: 700, fontSize: '0.9rem',
                                    cursor: loadingUpgrade === plan.key ? 'not-allowed' : 'pointer',
                                    opacity: loadingUpgrade === plan.key ? 0.7 : 1,
                                }}>
                                    {loadingUpgrade === plan.key ? 'Redirection...' : `${PLAN_LABELS[plan.key]} — ${plan.prix}€/mois`}
                                </button>
                            ))}
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
};

// ─── Page Pricing principale ──────────────────────────────────────────────────
const Pricing = () => {
    const history = useHistory();
    const [nbEcransSup,  setNbEcransSup]  = useState({});
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showModal,    setShowModal]    = useState(false);

    const isConnected    = !!getCookie('user_apiToken');
    const entrepriseCode = getEntrepriseCode();

    const getNbSup = (key) => nbEcransSup[key] || 0;

    const handleSelect = (plan) => {
        setSelectedPlan(plan);
        setShowModal(true);
    };

    const handleLogout = () => {
        document.cookie.split(';').forEach(c => {
            const name = c.split('=')[0].trim();
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure`;
        });
        window.location.href = '/';
    };

    const handleRetour = () => {
        history.push('/app');
    };

    // ── Mode connecté : afficher la gestion d'abonnement ──
    if (isConnected) {
        return (
            <GestionAbonnement
                entrepriseCode={entrepriseCode}
                onLogout={handleLogout}
                onRetour={handleRetour}
            />
        );
    }

    // ── Mode non-connecté : page marketing ──
    return (
        <div style={{ minHeight: '100vh', background: SEIZO_BG, fontFamily: FONT }}>
            {/* Header */}
            <header style={{ background: CARD_BG, borderBottom: '1px solid #e8edf2', padding: '1rem 2rem', display: 'flex', alignItems: 'center' }}>
                <a href={process.env.REACT_APP_LANDING_URL || 'https://www.seizo.fr'} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                    <img src={logoSeizo} alt="SEIZO" style={{ height: '40px', width: 'auto', display: 'block' }} />
                </a>
                <button onClick={() => history.push('/')} style={{
                    marginLeft: 'auto',
                    background: SEIZO_BLUE, border: 'none', borderRadius: '8px',
                    padding: '0.55rem 1.4rem', cursor: 'pointer', fontSize: '0.9rem',
                    color: '#fff', fontFamily: FONT, fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(0,176,255,0.25)', transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                    → Se connecter
                </button>
            </header>

            {/* Hero */}
            <div style={{ textAlign: 'center', padding: '3.5rem 1rem 2rem' }}>
                <h1 style={{ fontSize: '2.2rem', fontWeight: '800', color: TEXT_DARK, margin: '0 0 0.8rem', fontFamily: FONT }}>
                    Choisissez votre offre SEIZO
                </h1>
                <p style={{ fontSize: '1rem', color: TEXT_LIGHT, maxWidth: '520px', margin: '0 auto', lineHeight: 1.6, fontFamily: FONT }}>
                    Commencez gratuitement, évoluez selon vos besoins. Pas d'engagement, résiliez à tout moment.
                </p>
            </div>

            {/* Grille des plans */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', padding: '1rem 2rem 4rem', flexWrap: 'wrap', maxWidth: '1100px', margin: '0 auto' }}>
                {PLANS.map(plan => (
                    <PlanCard
                        key={plan.key}
                        plan={plan}
                        nbEcransSup={getNbSup(plan.key)}
                        onChangeSup={(n) => setNbEcransSup(prev => ({ ...prev, [plan.key]: n }))}
                        onSelect={handleSelect}
                    />
                ))}
            </div>

            {/* FAQ */}
            <div style={{ background: '#fff', borderTop: '1px solid #e8edf2', padding: '3rem 2rem' }}>
                <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                    <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: TEXT_DARK, marginBottom: '2rem', textAlign: 'center', fontFamily: FONT }}>Questions fréquentes</h2>
                    {[
                        { q: "Puis-je changer d'offre à tout moment ?", r: "Oui, vous pouvez upgrader votre abonnement depuis Mon Abonnement (accessible via le menu ou /pricing)." },
                        { q: "Comment fonctionne l'essai gratuit 14 jours ?", r: "Pour l'offre Premium, vous ne serez pas débité pendant 14 jours. Vous pouvez annuler avant la fin de la période d'essai sans frais." },
                        { q: "Qu'est-ce qu'un écran supplémentaire ?", r: "Un écran correspond à un totem SEIZO affiché dans votre organisation. Vous pouvez en ajouter autant que nécessaire à 39€/mois par écran." },
                        { q: 'Mes données sont-elles sécurisées ?', r: 'Oui, les paiements sont gérés par Stripe (certifié PCI DSS). Vos données sont hébergées en Europe.' },
                    ].map(({ q, r }) => (
                        <div key={q} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1.2rem', marginBottom: '1.2rem' }}>
                            <div style={{ fontWeight: '700', color: TEXT_DARK, fontSize: '0.9rem', marginBottom: '0.4rem', fontFamily: FONT }}>{q}</div>
                            <div style={{ color: TEXT_LIGHT, fontSize: '0.85rem', lineHeight: 1.6, fontFamily: FONT }}>{r}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <footer style={{ background: '#fff', borderTop: '1px solid #e8edf2', padding: '1.5rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <a href={process.env.REACT_APP_LANDING_URL || 'https://www.seizo.fr'} target="_blank" rel="noopener noreferrer">
                    <img src={logoSeizo} alt="SEIZO" style={{ height: '28px', width: 'auto' }} />
                </a>
                <div style={{ display: 'flex', gap: '1.5rem' }}>
                    <a href="/mentions-legales" style={{ color: TEXT_LIGHT, textDecoration: 'none', fontSize: '0.78rem', fontFamily: FONT }}>Mentions légales</a>
                    <a href={`${process.env.REACT_APP_LANDING_URL || 'https://www.seizo.fr'}/politique-confidentialite`} target="_blank" rel="noopener noreferrer" style={{ color: TEXT_LIGHT, textDecoration: 'none', fontSize: '0.78rem', fontFamily: FONT }}>Politique de confidentialité</a>
                    <a href={`${process.env.REACT_APP_LANDING_URL || 'https://www.seizo.fr'}/cgu`} target="_blank" rel="noopener noreferrer" style={{ color: TEXT_LIGHT, textDecoration: 'none', fontSize: '0.78rem', fontFamily: FONT }}>CGU</a>
                    <a href={`${process.env.REACT_APP_LANDING_URL || 'https://www.seizo.fr'}/cgv`} target="_blank" rel="noopener noreferrer" style={{ color: TEXT_LIGHT, textDecoration: 'none', fontSize: '0.78rem', fontFamily: FONT }}>CGV</a>
                    <a href="mailto:contact@seizo.fr" style={{ color: TEXT_LIGHT, textDecoration: 'none', fontSize: '0.78rem', fontFamily: FONT }}>Contact</a>
                </div>
                <div style={{ fontSize: '0.75rem', color: TEXT_LIGHT, fontFamily: FONT }}>© 2026 SEIZO. Tous droits réservés.</div>
            </footer>

            {/* Modal */}
            {showModal && selectedPlan && (
                <ModalCompte
                    plan={selectedPlan}
                    nbEcransSup={getNbSup(selectedPlan.key)}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

export default Pricing;
